import { useEffect, useState } from "react";
import { api, clearToken, setToken, errorText } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Loader2, KeyRound } from "lucide-react";
import { DoctorsAdmin } from "@/components/admin/DoctorsAdmin";
import { BookingsAdmin } from "@/components/admin/BookingsAdmin";
import { PackagesAdmin } from "@/components/admin/PackagesAdmin";
import { PasswordDialog } from "@/components/admin/PasswordDialog";
import { LOGO_URL } from "@/data/content";

const TABS = [
  { id: "doctors", label: "Doctors" },
  { id: "bookings", label: "Enquiries" },
  { id: "packages", label: "Packages" },
];

const LoginScreen = ({ onLoggedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setToken(data.access_token);
      onLoggedIn(data);
    } catch (err) {
      toast.error(errorText(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-white border border-navy/10 p-10 shadow-[0_30px_70px_rgba(15,31,61,0.08)]"
      >
        <img src={LOGO_URL} alt="Swastik Healthcare" className="h-12 w-auto mb-8" />
        <p className="text-teal text-[10px] uppercase tracking-[0.3em] font-semibold">Admin Portal</p>
        <h1 className="font-display text-3xl text-navy mt-2 mb-8">Sign in to manage your site.</h1>

        <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Email</label>
        <input
          data-testid="admin-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mt-2 mb-6 border-0 border-b border-slate-300 px-0 py-3 bg-transparent focus:outline-none focus:border-teal text-navy"
          placeholder="admin@swastikhealthcare.in"
        />

        <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Password</label>
        <input
          data-testid="admin-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mt-2 mb-8 border-0 border-b border-slate-300 px-0 py-3 bg-transparent focus:outline-none focus:border-teal text-navy"
          placeholder="••••••••"
        />

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="btn-sheen w-full inline-flex items-center justify-center gap-3 bg-navy text-white py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-teal transition-colors duration-300 disabled:opacity-60"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          Sign In
        </button>
      </form>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("doctors");
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      /* token already invalid */
    }
    clearToken();
    setUser(false);
    toast.success("Signed out.");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <Loader2 className="animate-spin text-teal" size={28} />
      </div>
    );
  }

  if (!user) return <LoginScreen onLoggedIn={setUser} />;

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="bg-white border-b border-navy/10 sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Swastik Healthcare" className="h-10 w-auto" />
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-teal font-semibold">
              Admin Portal
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="admin-view-site"
              className="hidden sm:inline text-[11px] uppercase tracking-[0.2em] text-navy/70 hover:text-teal transition-colors"
            >
              View Site
            </a>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="admin-change-password-open"
              className="inline-flex items-center gap-2 border border-navy/20 text-navy px-3 py-2 text-[11px] uppercase tracking-[0.15em] hover:border-teal hover:text-teal transition-colors"
            >
              <KeyRound size={14} /> Password
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 bg-navy text-white px-3 py-2 text-[11px] uppercase tracking-[0.15em] hover:bg-teal transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 flex gap-6 border-t border-navy/5">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`py-3 text-[11px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
                tab === t.id ? "border-teal text-teal" : "border-transparent text-navy/60 hover:text-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10">
        {tab === "doctors" && <DoctorsAdmin />}
        {tab === "bookings" && <BookingsAdmin />}
        {tab === "packages" && <PackagesAdmin />}
      </main>

      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
